import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../lib/utils';
import {
  Package, CheckCircle2, AlertTriangle, Wrench, TrendingDown,
  Clock, Briefcase, MapPin, BarChart3, GraduationCap, Flower2, Building2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, PieChart, Pie, Cell, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLORS = ['#2E7D32', '#FF9800', '#1E88E5', '#9C27B0', '#E91E63', '#607D8B', '#FFC107', '#4CAF50'];

const PROGRAM_META = {
  school: { label: 'School', icon: GraduationCap, accent: 'bg-emerald-100 text-emerald-600' },
  women_entrepreneurship: { label: 'Women Entrepreneurship', icon: Flower2, accent: 'bg-pink-100 text-pink-600' },
  administration: { label: 'Administration', icon: Building2, accent: 'bg-purple-100 text-purple-600' },
};

const AssetsDashboardPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetch(); }, []);

  const fetch = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/assets/dashboard/summary`);
      setData(res.data);
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>;
  }
  if (!data) return null;

  const summaryCards = [
    { label: 'Total Assets', value: data.total, icon: Package, accent: 'bg-primary/10 text-primary', subtitle: formatCurrency(data.total_value) },
    { label: 'Assigned / Active', value: data.assigned, icon: CheckCircle2, accent: 'bg-emerald-100 text-emerald-600' },
    { label: 'In Storage', value: data.in_stock, icon: Briefcase, accent: 'bg-blue-100 text-blue-600' },
    { label: 'Under Repair', value: data.under_repair, icon: Wrench, accent: 'bg-amber-100 text-amber-600' },
    { label: 'Low Stock Items', value: data.low_stock_count, icon: TrendingDown, accent: 'bg-red-100 text-red-600' },
    { label: 'Expiring Warranties', value: data.expiring_warranties, icon: AlertTriangle, accent: 'bg-orange-100 text-orange-600', subtitle: 'next 30 days' },
    { label: 'Pending Maintenance', value: data.pending_maintenance, icon: Clock, accent: 'bg-purple-100 text-purple-600' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading text-2xl font-semibold">Asset & Inventory Dashboard</h2>
        <p className="text-muted-foreground">Real-time overview of organizational assets, stock and maintenance</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="assets-summary-cards">
        {summaryCards.map((c) => {
          const Icon = c.icon;
          return (
            <Card key={c.label} className="card-hover">
              <CardContent className="p-4">
                <div className={`p-2 rounded-lg w-fit ${c.accent}`}><Icon className="h-4 w-4" /></div>
                <p className="text-xs text-muted-foreground mt-2">{c.label}</p>
                <p className="text-2xl font-heading font-bold">{c.value}</p>
                {c.subtitle && <p className="text-xs text-muted-foreground">{c.subtitle}</p>}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Assets by Program */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-lg">Assets by Program</CardTitle>
          <CardDescription>Distribution across organizational programs</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(PROGRAM_META).map(([k, m]) => {
              const Icon = m.icon;
              return (
                <Link key={k} to={`/assets/list?program=${k}`} className="p-4 rounded-xl border hover:shadow-md transition-all">
                  <div className="flex items-center justify-between mb-2">
                    <div className={`p-2 rounded-lg ${m.accent}`}><Icon className="h-4 w-4" /></div>
                    <Badge variant="outline">{data.by_program?.[k] || 0}</Badge>
                  </div>
                  <p className="font-medium">{m.label}</p>
                  <p className="text-xs text-muted-foreground mt-1">{data.by_program?.[k] || 0} assets registered</p>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2"><MapPin className="h-4 w-4" /> Distribution by Location</CardTitle>
            <CardDescription>Where assets are physically deployed</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {data.by_location.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.by_location.map(l => ({ ...l, name: l.name.replace(/_/g, ' ') }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#2E7D32" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data</div>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base flex items-center gap-2"><BarChart3 className="h-4 w-4" /> Asset Condition Status</CardTitle>
            <CardDescription>Overall fleet health</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {data.by_condition.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={data.by_condition}
                      cx="50%" cy="50%"
                      innerRadius={60} outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {data.by_condition.map((entry, i) => <Cell key={`cell-${entry.name || i}`} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No data</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Monthly Asset Purchases</CardTitle>
            <CardDescription>Acquisition trend over the last 12 months</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {data.monthly_purchases.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={data.monthly_purchases}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip formatter={(v, n) => n === 'value' ? formatCurrency(v) : v} />
                    <Legend />
                    <Bar dataKey="count" name="Items" fill="#1E88E5" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="value" name="Value (UGX)" fill="#FF9800" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No purchases recorded</div>}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="font-heading text-base">Maintenance Costs Over Time</CardTitle>
            <CardDescription>Repair & service expenditure</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              {data.maintenance_costs.length > 0 ? (
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={data.maintenance_costs}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={(v) => `${(v/1000).toFixed(0)}K`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Line type="monotone" dataKey="cost" stroke="#E91E63" strokeWidth={2} dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : <div className="h-full flex items-center justify-center text-muted-foreground text-sm">No maintenance recorded</div>}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Low stock list */}
      {data.low_stock_items.length > 0 && (
        <Card className="border-red-200 dark:border-red-900/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /> Low Stock Alerts</CardTitle>
              <Link to="/assets/inventory" className="text-xs text-primary hover:underline">View all →</Link>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {data.low_stock_items.map(i => (
                <div key={i.id} className="flex items-center justify-between p-2 rounded-lg bg-red-50 dark:bg-red-900/10">
                  <div>
                    <p className="font-medium text-sm">{i.name}</p>
                    <p className="text-xs text-muted-foreground">{i.category}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-red-600">{i.quantity} {i.unit || 'pcs'}</p>
                    <p className="text-xs text-muted-foreground">min {i.reorder_threshold}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AssetsDashboardPage;
